// src/App.js
import { useState } from "react";
import SuggestionBox from "./components/SuggestionBox";
import { useAuth } from "./context/AuthContext";
import { useLanguage } from "./context/LanguageContext";
import { STYLES, CATEGORIES } from "./data/styles";
import QuickLaunch from "./components/QuickLaunch";
import Header from "./components/Header";

export default function App() {
  const [copied, setCopied]               = useState(null);
  const [search, setSearch]               = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [showUpgrade, setShowUpgrade]     = useState(false);
  const [showQuickLaunch, setShowQuickLaunch] = useState(false);
  const [orientation, setOrientation] = useState("default");

  const { t } = useLanguage();

  const {
  user,
  canUsePrompt,
  incrementPromptUsage,
  } = useAuth();

  // Filter styles
  const filtered = STYLES.filter(s => {
    const matchCat    = activeCategory === "All" || s.category === activeCategory;
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  // Preservation clause — auto append ke semua prompt
  const PRESERVATION = "Preserve the original composition, subject, pose, facial features, background, and all elements exactly as in the original image. Only change the artistic style — do not add, remove, or alter any content, environment, or subject matter.";

  const ORIENTATION_TEXT = {
    default: "",
    landscape: "Output in landscape 16:9 aspect ratio.",
    portrait: "Output in portrait 9:16 aspect ratio.",
};

// Handle copy
const handleCopy = async (style) => {
  if (!user) {
    alert(t("login_first"));
    return;
  }
  if (!canUsePrompt) {
    setShowUpgrade(true);
    return;
  }

  // Gabung: prompt asal + preservation + orientation
  const orientationText = ORIENTATION_TEXT[orientation];
  const fullPrompt = [
    style.prompt,
    PRESERVATION,
    orientationText,
  ].filter(Boolean).join(" ");

  navigator.clipboard.writeText(fullPrompt).then(async () => {
    setCopied(style.id);
    setTimeout(() => setCopied(null), 2000);
    await incrementPromptUsage();
    setShowQuickLaunch(true); // ← tambah ni
  });
};

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;700&display=swap');
        *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: #0c0c0f; color: #e8e8f0; font-family: 'Inter', sans-serif; min-height: 100vh; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #0c0c0f; }
        ::-webkit-scrollbar-thumb { background: #2a2a3a; border-radius: 3px; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes copyPop { 0% { transform: scale(1); } 50% { transform: scale(1.05); } 100% { transform: scale(1); } }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#0c0c0f" }}>

        {/* HEADER */}
        <Header onUpgradeClick={() => setShowUpgrade(true)} />

        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "48px 24px 32px" }}>

          {/* HERO */}
          <div style={{ textAlign: "center", marginBottom: 40, animation: "fadeIn 0.5s ease" }}>
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 20, background: "rgba(99,102,241,0.1)", border: "1px solid rgba(99,102,241,0.3)", color: "#a5b4fc", fontSize: 12, letterSpacing: 2, textTransform: "uppercase", marginBottom: 20 }}>
              {t("hero_badge")}
            </div>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 700, lineHeight: 1.15, letterSpacing: "-1px", marginBottom: 14 }}>
              {t("hero_title_1")}<br />
              <span style={{ background: "linear-gradient(90deg, #6366f1, #a855f7, #ec4899)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{t("hero_title_2")}</span>
            </h1>
            <p style={{ color: "#666", fontSize: 16, maxWidth: 480, margin: "0 auto" }}>
              {t("hero_subtitle")}
            </p>
          </div>

          {/* SEARCH */}
          <div style={{ maxWidth: 480, margin: "0 auto 28px", position: "relative" }}>
            <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#444" }}>🔍</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("search_placeholder")}
              style={{ width: "100%", padding: "12px 16px 12px 42px", background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 12, color: "#e8e8f0", fontSize: 14, outline: "none", fontFamily: "inherit" }}
            />
          </div>

          {/* ORIENTATION SELECTOR */}
          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
            {[
              { key: "default", label: t("orient_default"), icon: "⬜" },
              { key: "landscape", label: t("orient_landscape"), icon: "🖥️" },
              { key: "portrait", label: t("orient_portrait"), icon: "📱" },
            ].map(o => (
              <button
                key={o.key}
                onClick={() => setOrientation(o.key)}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "7px 16px", borderRadius: 20, fontFamily: "inherit",
                  border: orientation === o.key ? "none" : "1px solid #2a2a3a",
                  background: orientation === o.key ? "rgba(99,102,241,0.2)" : "#13131a",
                  color: orientation === o.key ? "#a5b4fc" : "#888",
                  fontSize: 13, fontWeight: 500, cursor: "pointer",
                  outline: orientation === o.key ? "1px solid #6366f1" : "none",
                }}
              >
                <span>{o.icon}</span>
                <span>{o.label}</span>
              </button>
            ))}
          </div>

          {/* CATEGORIES */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center", marginBottom: 32 }}>
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                style={{ padding: "7px 16px", borderRadius: 20, border: activeCategory === cat ? "none" : "1px solid #2a2a3a", background: activeCategory === cat ? "linear-gradient(135deg, #6366f1, #a855f7)" : "#13131a", color: activeCategory === cat ? "#fff" : "#888", fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
                {t("cat_" + cat.toLowerCase())}
              </button>
            ))}
          </div>

          <div style={{ textAlign: "center", fontSize: 13, color: "#444", marginBottom: 24 }}>
            {t("styles_available").replace("{n}", filtered.length)}
          </div>

          {/* GRID */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14 }}>
            {filtered.map((style, i) => (
              <div
                key={style.id}
                onClick={() => handleCopy(style)}
                style={{ background: "#13131a", borderRadius: 16, border: copied === style.id ? "1px solid #10b981" : "1px solid #1e1e2e", overflow: "hidden", cursor: "pointer", transition: "all 0.2s", animation: `fadeIn 0.4s ease ${i * 0.02}s both` }}
                onMouseEnter={e => { e.currentTarget.style.border = "1px solid #6366f1"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.border = copied === style.id ? "1px solid #10b981" : "1px solid #1e1e2e"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ width: "100%", aspectRatio: "4/5", background: "linear-gradient(135deg, #1a1a2e, #16213e)", position: "relative", overflow: "hidden" }}>
                  {style.image ? (
                    <img
                      src={style.image}
                      alt={style.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "flex";
                      }}
                    />
                  ) : null}
                  <div style={{ display: style.image ? "none" : "flex", width: "100%", height: "100%", alignItems: "center", justifyContent: "center", position: style.image ? "absolute" : "relative", inset: 0 }}>
                    <span style={{ fontSize: 52, filter: "drop-shadow(0 0 16px rgba(99,102,241,0.4))" }}>{style.emoji}</span>
                  </div>
                  {copied === style.id && (
                    <div style={{ position: "absolute", inset: 0, background: "rgba(16,185,129,0.15)", display: "flex", alignItems: "center", justifyContent: "center", animation: "copyPop 0.3s ease" }}>
                      <div style={{ background: "#10b981", borderRadius: 20, padding: "6px 16px", fontSize: 13, fontWeight: 700, color: "#fff" }}>✓ Copied!</div>
                    </div>
                  )}
                  <div style={{ position: "absolute", bottom: 8, right: 8, background: "rgba(0,0,0,0.6)", borderRadius: 6, padding: "3px 8px", fontSize: 10, color: "#888" }}>{t("cat_" + style.category.toLowerCase())}</div>
                </div>
                <div style={{ padding: "12px", textAlign: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{style.name}</div>
                  <div style={{ fontSize: 11, color: "#555", marginTop: 4 }}>
                    {user ? t("click_to_copy") : t("login_to_copy")}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#444" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
              <div>{t("no_styles").replace("{search}", search)}</div>
            </div>
          )}

          {/* HOW TO USE */}
          <div style={{ marginTop: 64, padding: "32px", background: "#13131a", borderRadius: 20, border: "1px solid #1e1e2e" }}>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 24, textAlign: "center" }}>{t("how_to_use")}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 20 }}>
              {[
                { step: "1", title: t("step1_title"), desc: t("step1_desc"), icon: "🔑" },
                { step: "2", title: t("step2_title"), desc: t("step2_desc"), icon: "🎨" },
                { step: "3", title: t("step3_title"), desc: t("step3_desc"), icon: "📋" },
                { step: "4", title: t("step4_title"), desc: t("step4_desc"), icon: "✨" },
              ].map(item => (
                <div key={item.step} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>{item.icon}</div>
                  <div style={{ fontSize: 11, color: "#6366f1", fontWeight: 700, letterSpacing: 2, marginBottom: 6 }}>{t("step_label")} {item.step}</div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{item.title}</div>
                  <div style={{ fontSize: 12, color: "#666", lineHeight: 1.5 }}>{item.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* SUGGESTION BOX */}
          <SuggestionBox user={user} />

          <div style={{ textAlign: "center", marginTop: 40, marginBottom: 24 }}>
          <div style={{ fontSize: 13, color: "#333", marginBottom: 6 }}>
            © 2026 Promptix by imtechinnovations.com
          </div>
          <div style={{ fontSize: 11, color: "#444", marginBottom: 8 }}>
            IMTECH INNOVATIONS | 202403257722 (003652727-H)
          </div>
          <a href="/terms" style={{ fontSize: 11, color: "#555", textDecoration: "none" }}>
            Terms of Service & Privacy Policy
          </a>
        </div>
        </div>
      </div>

      {/* UPGRADE MODAL */}
      {showUpgrade && (
        <div onClick={() => setShowUpgrade(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#13131a", border: "1px solid #2a2a3a", borderRadius: 24, padding: "32px 28px", maxWidth: 480, width: "100%", animation: "fadeIn 0.2s ease" }}>

            <div style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>✦</div>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
                {t("modal_title")}
              </h2>
              <p style={{ color: "#555", fontSize: 13 }}>{t("modal_subtitle")}</p>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>

              {/* Starter */}
              <div
                onClick={() => { window.location.href = `https://pay.bcl.my/STARTER_LINK?ref=${user?.uid}`; }}
                style={{ background: "#0c0c0f", border: "1px solid #2a2a3a", borderRadius: 14, padding: "16px 18px", cursor: "pointer", transition: "border 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.border = "1px solid #444"}
                onMouseLeave={e => e.currentTarget.style.border = "1px solid #2a2a3a"}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t("tier_starter")}</div>
                    <div style={{ fontSize: 13, color: "#f59e0b", fontWeight: 600, marginTop: 3 }}>{t("starter_value")}</div>
                    <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{t("valid_30days")}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>RM 4.90</div>
                    <div style={{ fontSize: 11, color: "#444" }}>{t("one_time")}</div>
                  </div>
                </div>
              </div>

              {/* Popular (Decoy) */}
              <div
                onClick={() => { window.location.href = `https://pay.bcl.my/POPULAR_LINK?ref=${user?.uid}`; }}
                style={{ background: "#0c0c0f", border: "1px solid #2a2a3a", borderRadius: 14, padding: "16px 18px", cursor: "pointer", transition: "border 0.2s", position: "relative" }}
                onMouseEnter={e => e.currentTarget.style.border = "1px solid #444"}
                onMouseLeave={e => e.currentTarget.style.border = "1px solid #2a2a3a"}
              >
                <div style={{ position: "absolute", top: -10, left: 14, background: "#10b981", borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 700, color: "#fff" }}>
                  {t("badge_popular")}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t("tier_popular")}</div>
                    <div style={{ fontSize: 13, color: "#10b981", fontWeight: 600, marginTop: 3 }}>{t("popular_value")}</div>
                    <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>{t("valid_30days")}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>RM 8.90</div>
                    <div style={{ fontSize: 11, color: "#444" }}>{t("one_time")}</div>
                  </div>
                </div>
              </div>

              {/* Lifetime (Target) */}
              <div
                onClick={() => { window.location.href = `https://pay.bcl.my/LIFETIME_LINK?ref=${user?.uid}`; }}
                style={{ background: "rgba(99,102,241,0.08)", border: "2px solid #6366f1", borderRadius: 14, padding: "16px 18px", cursor: "pointer", position: "relative", transition: "all 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(99,102,241,0.14)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(99,102,241,0.08)"}
              >
                <div style={{ position: "absolute", top: -10, right: 14, background: "linear-gradient(135deg, #6366f1, #a855f7)", borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 700, color: "#fff" }}>
                  {t("badge_best")}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#a5b4fc" }}>{t("tier_lifetime")}</div>
                    <div style={{ fontSize: 13, color: "#a855f7", fontWeight: 700, marginTop: 3 }}>{t("lifetime_value")}</div>
                    <div style={{ fontSize: 11, color: "#6366f1", marginTop: 2 }}>{t("lifetime_sub")}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: "#fff" }}>RM 9.90</div>
                    <div style={{ fontSize: 11, color: "#6366f1" }}>{t("forever")}</div>
                  </div>
                </div>
              </div>

            </div>

            <p style={{ textAlign: "center", fontSize: 11, color: "#444", marginBottom: 16 }}>
              {t("modal_note")}
            </p>

            <button
              onClick={() => setShowUpgrade(false)}
              style={{ width: "100%", padding: "12px", background: "none", border: "1px solid #2a2a3a", borderRadius: 12, color: "#555", fontSize: 14, cursor: "pointer" }}
            >
              {t("maybe_later")}
            </button>
          </div>
        </div>
      )}
    <QuickLaunch show={showQuickLaunch} onClose={() => setShowQuickLaunch(false)} />
    </>
  );
}